/**
 * @author Martin Leonov <martinleonov@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-disable max-statements */
/* eslint-disable max-lines */
/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import { daySystemLocalEpoch, SimulatorAt } from './deterministic';
import { KeyedClipIteratorFactory } from './clip-iterator-factory';
import ErrorName from '../error/error-name';
import promisifyTimeWatcher from '../promisify/promisify-time-watcher';
import Queue from '../queue/queue';
import ScheduleInfo from '../schedule/schedule-info';
import ViewerDelayer from '../viewer/delayer/viewer-delayer';
import ViewerDelayerFactory from '../viewer/delayer/viewer-delayer-factory';

import hourglassSvg from './hourglass-solid.svg';
import fileCircleQuestionSvg from './file-circle-question-solid.svg';
import videoSlashSvg from './video-slash-solid.svg';

/**
 * Content controller pro deterministický playlist.
 * @implements {ViewerController}
 */
class PlaylistDeterministicViewerController {
  /**
   * Vytvoří instanci.
   * @param {AnalyticsTracker} analyticsTracker
   * @param {ViewerLifeCycleFactory} lifeCycleFactory
   * @param {DsStore} store
   * @param {UnixTimestamp} unixTimestamp
   * @param {ViewerSlideFactory} viewerSlideFactory
   */
  constructor(
    analyticsTracker,
    lifeCycleFactory,
    store,
    unixTimestamp,
    viewerSlideFactory
  ) {
    this.analyticsTracker = analyticsTracker;
    this.viewerLifeCycle = lifeCycleFactory.create();
    this.store = store;
    this.unixTimestamp = unixTimestamp;
    this.viewerSlideFactory = viewerSlideFactory;

    this.emitter = new EventEmitter();
    this.abortController = new window.AbortController();
    this.signal = this.abortController.signal;
    this.loadRequested = false;
    this.playRequested = false;
    this.stopRequested = false;
    this.volume = 1;

    this.playingSlide = null;
    this.loadingSlide = null;
    this.overlayElem = null;
    this.timeout = null;
    this.unixTimeOffset = 0;
    this.delayer = new ViewerDelayerFactory(ViewerDelayer).create();
    this.playQueue = new Queue();

    this.onSlideError = this.onSlideError.bind(this);
  }

  /**
   * Přesune načítaný slajd do přehrávaného.
   * @private
   * @returns {undefined} Nic.
   */
  activateLoading() {
    this.playingSlide = this.loadingSlide;
    this.loadingSlide = null;
  }

  /**
   * Naváže naslouchání událostem ze slajdu.
   * @param {ViewerSlide} slide Slajd, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  bindSlide(slide) {
    slide.on('error', this.onSlideError);
  }

  /**
   * Vytvoří nový načítaný slajd.
   * @param {ScheduleInfo} scheduleInfo Naplánované médium, které se má zobrazit ve slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po vytvoření.
   */
  createLoading(scheduleInfo) {
    return new Promise((resolve) => {
      const slide = this.createSlide(scheduleInfo);
      this.loadingSlide = slide;
      return resolve(slide.seize());
    });
  }

  /**
   * Vytvoří nový přehrávaný slajd.
   * @param {ScheduleInfo} scheduleInfo Naplánované médium, které se má zobrazit ve slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po vytvoření.
   */
  createPlaying(scheduleInfo) {
    return new Promise((resolve) => {
      const slide = this.createSlide(scheduleInfo);
      this.playingSlide = slide;
      return resolve(slide.seize());
    });
  }

  /**
   * Vytvoří nový slajd a naváže naslouchání jeho událostem.
   * @param {ScheduleInfo} scheduleInfo Naplánované médium, které se má zobrazit ve slajdu.
   * @private
   * @returns {ViewerSlide} Vytvořený slajd.
   */
  createSlide(scheduleInfo) {
    const slide = this.viewerSlideFactory.create(this.el, scheduleInfo);
    this.bindSlide(slide);
    return slide;
  }

  /**
   * Odebere zdroje načítaného slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  deleteLoading() {
    return this.deleteSlide(this.loadingSlide).then(() => {
      this.loadingSlide = null;
    });
  }

  /**
   * Odebere zdroje přehrávaného slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  deletePlaying() {
    return this.deleteSlide(this.playingSlide).then(() => {
      this.playingSlide = null;
    });
  }

  /**
   * Odebere zdroje slajdu a zruší ho.
   * @param {ViewerSlide} slide Slajd k odebrání.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  deleteSlide(slide) {
    return new Promise((resolve) =>
      resolve(slide.delete().then(() => this.destroySlide(slide)))
    );
  }

  /**
   * Postupně odebere požadavky na přechody z fronty a provede je.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po provedení všech přechodů ve frontě.
   */
  dequeue() {
    return new Promise((resolve) => {
      if (this.stopRequested) {
        this.playRequested = false;
        this.loadRequested = false;
        return resolve(this.unsetLoading().then(() => this.unsetPlaying()));
      }
      if (this.playRequested) {
        this.playRequested = false;
        this.loadRequested = false;

        const now = this.now();
        const spot = this.simulatorAt.getAt(now);
        return resolve(
          this.playUnsafe(spot).then(() => {
            const { delayer } = this;
            return delayer
              .succeed()
              .then(() => this.wait())
              .then(() => this.loadUnsafe(spot));
          })
        );
      }

      return resolve();
    });
  }

  /** @inheritdoc */
  destroy() {
    // Prázdná metoda pro splnění rozhraní.
  }

  /**
   * Zruší slajd a naslouchání jeho událostem.
   * @param {ViewerSlide} slide Slajd ke zrušení.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  destroySlide(slide) {
    this.unbindSlide(slide);
    slide.destroy();
  }

  /**
   * Zařadí požadavek na přechod do fronty.
   * @private
   * @returns {Promise.<undefined>}
   * Pokud není jiný požadavek ve frontě, vyřeší se po provedení požadavku a všech ostatních, které se za něj zařadí.
   * Pokud je jiný požadavek ve frontě, vyřeší se po jeho zařazení do fronty.
   */
  enqueue() {
    return new Promise((resolve) =>
      resolve(this.playQueue.enqueue(() => this.dequeue()))
    );
  }

  /** @inheritdoc */
  enter() {
    // eslint-disable-next-line arrow-body-style
    return this.viewerLifeCycle.enter(() => {
      // Nečeká se na výsledek
      return this.play();
    });
  }

  /**
   * Zobrazí přehrávaný slajd.
   * @param {Spot} spot spot který se bude přehrávat.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po zobrazení.
   */
  enterPlaying(spot) {
    const unixTimeUs = this.now();
    const { playingSlide } = this;
    return new Promise((resolve) =>
      resolve(playingSlide.enter((unixTimeUs - spot.time) / 1000))
    );
  }

  /**
   * Vygeneruje HTMLElement s piktogramem.
   * @param {Error} err Typ chyby, pro který se má vytvořit piktogram.
   * @param {string} message Textová zpráva pro zobrazení pod piktogramem.
   * @param {ScheduleInfo} scheduleInfo Aktuálně přehrávaný obsah.
   * @returns {HTMLElement} Vytvořený element s piktogramem.
   */
  generatePlayErrorElem(err, message, scheduleInfo) {
    let imageSrc;
    switch (err.name) {
      case ErrorName.SeekError:
        imageSrc = hourglassSvg;
        break;
      case ErrorName.UnsupportedMimeTypeError:
        imageSrc = videoSlashSvg;
        break;
      default:
        imageSrc = fileCircleQuestionSvg;
        break;
    }

    const iconWrapper = document.createElement('div');
    const flexCenter = {
      justifyContent: 'center',
      alignItems: 'center',
      flexFlow: 'column',
      webkitJustifyContent: 'center',
      webkitAlignItems: 'center',
      webkitFlexFlow: 'column'
    };
    const fullSize = { height: '100%', width: '100%' };
    iconWrapper.setAttribute('style', 'display: flex; display: -webkit-flex;');
    Object.assign(iconWrapper.style, {
      ...flexCenter,
      position: 'absolute',
      top: scheduleInfo.getTop(),
      left: scheduleInfo.getLeft(),
      zIndex: scheduleInfo.getZIndex(),
      width: scheduleInfo.getWidth(),
      height: scheduleInfo.getHeight()
    });

    // Div se symbolem
    const iconDiv = document.createElement('div');
    iconDiv.setAttribute('style', 'display: flex; display: -webkit-flex;');

    Object.assign(iconDiv.style, {
      ...flexCenter,
      ...fullSize,
      color: '#fff',
      flex: '1',
      webkitFlex: '1'
    });

    const img = document.createElement('img');
    Object.assign(img.style, {
      width: '15%',
      height: '15%'
    });
    img.src = imageSrc;
    iconDiv.appendChild(img);

    const textWrapper = document.createElement('div');
    textWrapper.setAttribute('style', 'display: flex; display: -webkit-flex;');
    Object.assign(textWrapper.style, {
      width: '100%',
      padding: '1rem',
      fontSize: '50%',
      justifyContent: 'center',
      alignItems: 'center',
      webkitJustifyContent: 'center',
      webkitAlignItems: 'center'
    });

    const textDiv = document.createElement('div');
    textDiv.textContent = message;
    textDiv.style.textAlign = 'center';
    textWrapper.appendChild(textDiv);

    iconWrapper.appendChild(iconDiv);

    iconDiv.appendChild(textWrapper);

    return iconWrapper;
  }

  /**
   * Ošetří vzniklou chybu.
   * @private
   * @returns {undefined} Nic.
   */
  handleError() {}

  /**
   * Zaznamená analytická data.
   * @param {ScheduleInfo} scheduleInfo Informace o přehraném médiu.
   * @returns {Promise.<AnalyticsCounter>} Agregovaná data, do kterých byl proveden záznam.
   */
  hit(scheduleInfo) {
    return this.analyticsTracker.hit(scheduleInfo);
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {});
  }

  /**
   * Vyhledá médium vhodné k načtení.
   * @param {number} time čas pro který se má provést preload.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po nastavení načtítaného média.
   */
  loadSpot(time) {
    const spot = this.simulatorAt.getAt(time);
    const scheduleInfo = this.scheduleInfoFromSpot(spot);

    return new Promise((resolve) => {
      if (isNil(scheduleInfo)) {
        // Není žádný obsah předurčen k preload
        return resolve();
      }
      const playingScheduleInfo = this.playingSlide?.getScheduleInfo();
      if (playingScheduleInfo?.playlist?.deterministic) {
        // Pokud je aktuálně přehravaný slide deterministicky playlist,
        // tak neprednacitej dal, protože dojde lehce k vyčerpání zdojů
        return resolve();
      }
      if (!scheduleInfo.isPreloadable()) {
        // Médium není určeno k preloadu.
        // Vyhledávání se ukončí a načítaný slajd se ponechá, třeba se bude hodit.
        // Až se na médium provede přechod, tak se zobrazí rovnou bez preloadu.
        return resolve();
      }
      const { loadingSlide } = this;

      if (
        !isNil(loadingSlide) &&
        scheduleInfo.isEqual(loadingSlide.getScheduleInfo())
      ) {
        // V načítaném slajdu je již požadované médium připraveno.
        // Vyhledávání se ukončí a načítaný slajd se ponechá, když je již správně připraven.
        return resolve();
      }

      // Je nutné nastavit nový načítaný slajd.
      return resolve(
        this.unsetLoading().then(() => this.setLoading(scheduleInfo))
      );
    });
  }

  /**
   * Načte další médium bez ošetření paralelního spuštění.
   * @param {Spot} currentSpot Aktuálně přehrávaný spot.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po nastavení načtítaného média.
   */
  loadUnsafe(currentSpot) {
    // Preload dalšího media ja čas spuštění aktuálně přehrávaného media + jeho délka
    const { time } = currentSpot;
    const { duration } = currentSpot;
    return this.loadSpot(time + duration);
  }

  /**
   * Zjistí aktuální čas.
   * @returns {number} Aktuální čas v mikrosekundách.
   */
  now() {
    return (this.unixTimestamp.now() + this.unixTimeOffset) * 1000;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přehrání časovače média.
   * @param {ScheduleInfo} scheduleInfo Naplánované informace, které byly přehrány.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed(scheduleInfo) {
    const { playingSlide } = this;
    if (isNil(playingSlide)) {
      // Pokud nic nehrálo např. vyskytla se chyba, žádný media plán...
      this.play();
      return;
    }
    const playingScheduleInfo = playingSlide.getScheduleInfo();
    if (!playingScheduleInfo.isEqual(scheduleInfo)) {
      throw new Error('Unexpected played slide.');
    }
    playingSlide.setPlayed(true);
    this.play();
  }

  /**
   * Reakce na chybu ve slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onSlideError() {}

  /**
   * Přehraje další médium.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po nastavení přehrávaného média.
   */
  play() {
    this.unixTimeOffset = this.unixTimestamp.offset();
    return (
      new Promise((resolve) => {
        this.playRequested = true;
        return resolve(this.enqueue());
      })
        // TODO Nemělo by být toto ošetřeno spíše v on* metodách?
        .catch((error) => this.handleError(error))
    );
  }

  /**
   * Vyhledá médium vhodné k přehrání.
   * @private
   * @param {Spot} spot Informace o přehraném médiu.
   * @returns {Promise.<boolean>} Příznak, jestli bylo nastaveno přehrávané médium.
   */
  playSpot(spot) {
    return new Promise((resolve) => resolve(this.setPlaying(spot)));
  }

  /**
   * Přehraje další médium bez ošetření paralelního spuštění.
   * @private
   * @param {Spot} spot Informace o přehraném médiu.
   * @returns {Promise.<boolean>} Příznak, jestli bylo médium přehráno.
   */
  playUnsafe(spot) {
    return this.unsetPlaying().then(() => this.playSpot(spot));
  }

  /**
   * Uvolní zdroje načítaného slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po uvolnění.
   */
  releaseLoading() {
    return this.releaseSlide(this.loadingSlide).then(() => {
      this.loadingSlide = null;
    });
  }

  /**
   * Uvolní zdroje přehrávaného slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po uvolnění.
   */
  releasePlaying() {
    return this.releaseSlide(this.playingSlide).then(() => {
      this.playingSlide = null;
    });
  }

  /**
   * Uvolní zdroje slajdu a uvolní naslouchání jeho událostem.
   * @param {ViewerSlide} slide Slajd k uvolnění.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po uvolnění.
   */
  releaseSlide(slide) {
    return new Promise((resolve) =>
      resolve(slide.release().then(() => this.unbindSlide(slide)))
    );
  }

  /**
   * Vytvoří ScheduleInfo ze Spotu.
   * @param {Spot} spot Aktuálně přehrávaný Spot.
   * @returns {ScheduleInfo} Namapované ScheduleInfo.
   */
  scheduleInfoFromSpot(spot) {
    const { content, volume } = spot;
    if (isNil(content)) {
      return null;
    }
    const scheduleInfo = new ScheduleInfo({
      session: this.session,
      viewerRegistry: this.viewerRegistry,
      store: this.store,
      layer: this.layer,
      layout: this.layout,
      ...content,
      playDuration: spot.duration / 1000,
      // Hlasitost rodiče.
      volume: volume * this.volume
    });

    return scheduleInfo;
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      this.el = viewerSource.parentEl;
      const { layer, layout, session, viewerRegistry, volume } = scheduleInfo;
      this.viewerRegistry = viewerRegistry;
      this.session = session;
      this.layer = layer;
      this.layout = layout;
      this.volume = volume;

      const allTracks = this.store.filter('track');
      const clipIteratorFactory = KeyedClipIteratorFactory.fromPlaylist(
        scheduleInfo.playlist,
        (playlistId) =>
          // TODO: #530
          allTracks.filter((t) => t.parentPlaylistId === playlistId)
      );
      this.simulatorAt = new SimulatorAt(
        clipIteratorFactory,
        daySystemLocalEpoch
      );
      this.emitter.emit('loaded');
    });
  }

  /**
   * Přidá načítaný slajd.
   * @param {ScheduleInfo} scheduleInfo Naplánované médium, které se má zobrazit ve slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po přidání.
   */
  setLoading(scheduleInfo) {
    return this.createLoading(scheduleInfo)
      .then(() => true)
      .catch(() => {
        // Ignorování chyb z ViewerSlide.
        // Zde je přítomno kvůli odfiltrování chyb vznilých při získávání
        // zdrojů (např. vyčerpání PDF).
        if (!isNil(this.loadingSlide)) {
          return this.deleteLoading().then(() => false);
        }
        return false;
      });
  }

  /**
   * Přidá přehrávaný slajd.
   * @param {Spot} spot Naplánované médium, které se má zobrazit ve slajdu.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po přidání.
   */
  setPlaying(spot) {
    const unixTimeUs = this.now();
    const { time, duration } = spot;

    const durationUs = time + duration - unixTimeUs;
    const durationMs = durationUs / 1000;

    let hasError = false;
    const scheduleInfo = this.scheduleInfoFromSpot(spot);

    const { signal } = this;
    // Spustí se asynchronně časovač
    promisifyTimeWatcher(spot.time + spot.duration, () => this.now(), {
      abortSignal: signal
    })
      .then(() => {
        // Pokud se vyskytla chyba nebo se nepřehrával žádný obsah
        if (hasError || isNil(scheduleInfo)) {
          return;
        }

        // Odeslání pouze kompletně přehraného media do analytiky,
        // v případě se při přehrávání vyskytla chyba,
        // tak se medium neodesílá do analytiky.
        this.hit(scheduleInfo);
      })
      .catch((err) => {
        if (err.name !== ErrorName.AbortError) {
          throw err;
        }
      })
      .finally(() => {
        this.removeElemFromDOM();
        if (!this.isReleased) {
          this.onPlayed(scheduleInfo);
        }
      });

    return new Promise((resolve) => {
      if (isNil(scheduleInfo)) {
        // TODO: ikona pro čekající na start mediaplanu, zatim je cerno
        return resolve();
      }
      // Kontrola jestli je doba přehrávání menší než zadaný interval.
      // Toto se kontroluje kvůli tomu, že na tak krátkou chvílí nemá cenu pouštět přehrávání.
      // TODO: jaký časový interval
      if (durationMs < 1000) {
        // Po tento interval chceme, aby hrálo černo
        return resolve();
      }
      const viewerType = scheduleInfo.getViewerType();
      const content = scheduleInfo.getMedium();
      if (!viewerType.isStatic(content)) {
        // Obsah není seekovatelný a zároveň začátek obsahu neodpovídá času spuštění.
        if (
          !viewerType.isSeekable(content) &&
          (this.now() - spot.time) / 1000 > 1000
        ) {
          const error = new Error('seek not supporeted, start too delayed');
          error.name = ErrorName.SeekError;
          throw error;
        }
      }

      const { loadingSlide } = this;
      if (
        !isNil(loadingSlide) &&
        scheduleInfo.isEqual(loadingSlide.getScheduleInfo())
      ) {
        this.activateLoading();
        return resolve(this.enterPlaying(spot));
      }
      return resolve(
        this.createPlaying(scheduleInfo).then(() => this.enterPlaying(spot))
      );
    }).catch((err) => {
      hasError = true;
      // Ignorování chyb z ViewerSlide.
      // Zde je přítomno kvůli odfiltrování HTMLMediaError, které vznikly
      // během enter (respektive play) u video ovladačů.

      if (err.name !== ErrorName.TooLatePlayError) {
        // Na základě chyby vygeneruje se element s vhodným piktogramem.
        this.overlayElem = this.generatePlayErrorElem(
          err,
          `${scheduleInfo.medium?.name}`,
          scheduleInfo
        );
        this.el.appendChild(this.overlayElem);
      }

      if (isNil(this.playingSlide)) {
        return;
      }
      this.playingSlide.setFailed(true);
      return this.deletePlaying();
    });
  }

  /**
   * Zastaví zobrazování obsahu ve vláknu.
   * @returns {Promise.<undefined>} Vyřeší se po zastavení a uvolnění všech zdrojů.
   */
  stop() {
    this.stopRequested = true;
    return this.enqueue();
  }

  /**
   * Odstraní zobrazení piktogram z DOM.
   * @private
   * @returns {void}
   */
  removeElemFromDOM() {
    if (isNil(this.overlayElem)) {
      return;
    }
    try {
      this.el.removeChild(this.overlayElem);
    } catch {}
    this.overlayElem = null;
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      this.isReleased = true;
      // Zrušení časovače
      this.abortController.abort();
      return this.stop();
    });
  }

  /**
   * Uvolní naslouchání událostem ze slajdu.
   * @param {ViewerSlide} slide Slajd, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindSlide(slide) {
    slide.off('error', this.onSlideError);
  }

  /**
   * Odebere načítaný slajd.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  unsetLoading() {
    return new Promise((resolve) => {
      const { loadingSlide } = this;
      if (isNil(loadingSlide)) {
        return resolve();
      }
      if (loadingSlide.isFailed()) {
        return resolve(this.deleteLoading());
      }
      return resolve(
        this.releaseLoading().catch((error) => {
          this.handleError(error);
          return this.deleteLoading();
        })
      );
    });
  }

  /**
   * Odebere přehrávaný slajd.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po odebrání.
   */
  unsetPlaying() {
    return new Promise((resolve) => {
      this.removeElemFromDOM();

      const { playingSlide } = this;
      if (isNil(playingSlide)) {
        return resolve();
      }
      if (playingSlide.isFailed()) {
        return resolve(this.deletePlaying());
      }
      return resolve(
        playingSlide
          .leave()
          .then(() => this.releasePlaying())
          .catch((error) => {
            this.handleError(error);
            return this.deletePlaying();
          })
      );
    });
  }

  /**
   * Aplikuje zpoždění vyžadované platformou Tizen.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po zpoždění.
   */
  wait() {
    // TODO Platformově specifické prolínače. Aplikovat zpoždění pouze pro platformu Tizen.
    return new Promise((resolve) => {
      // Délka zpoždění byla zjištěna experimentálně na TV.
      this.timeout = setTimeout(resolve, 200);
    });
  }
}

export default PlaylistDeterministicViewerController;
